import { useEffect, useMemo, useState } from 'react';
import { formatPercent } from '../SharedAccountBody';
import ComparisonCardSkeleton from './ComparisonCardSkeleton';
import ComparisonHoverTooltip from './ComparisonHoverTooltip';
import { isComparisonContentLoading } from './comparisonLoading';
import type { ComparisonAccount } from './comparisonTypes';
import { positionRowTooltipContent, sharedHoldingRowTooltipContent } from './comparisonTooltipContent';
import {
  buildAllocationOverlap,
  buildPositionDiff,
  lookupCompanyName,
  lookupPositionMeta,
} from './comparisonUtils';

const SHARED_HOLDINGS_COLOR = '#2ee66b';
const UNIQUE_PREVIEW_COUNT = 8;
const SHARED_PREVIEW_COUNT = 12;

const CARD_CLASS =
  'rounded-[12px] border border-[color:var(--nq-line)] bg-[color:var(--nq-surface-card)] px-[18px] py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]';

const CHIP_DOT = 'inline-block h-2 w-2 shrink-0 rounded-full';
const TICKER = 'font-mono text-[0.85rem] font-semibold tracking-[0.02em]';
const METRIC =
  'flex min-w-[72px] flex-none flex-col items-end gap-px text-right tabular-nums';
const UNIQUES_PORTFOLIO = 'text-[0.85rem] font-semibold';
const MORE_BTN =
  'm-0 block w-full cursor-pointer border-none bg-transparent p-0 text-left text-[0.78rem] italic text-white/45 hover:text-white/[0.72] focus-visible:rounded-[4px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary/55';

const renderPlPct = (pct: number | undefined) => {
  if (pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span
      className={`text-[0.78rem] font-medium ${up ? 'text-positive' : 'text-negative'}`}
    >
      {formatPercent(pct)}
    </span>
  );
};

const ComparisonAllocationOverlap = ({
  accounts,
}: {
  accounts: ComparisonAccount[];
}) => {
  const diff = useMemo(() => buildPositionDiff(accounts), [accounts]);
  const overlap = useMemo(
    () => buildAllocationOverlap(diff, accounts),
    [diff, accounts],
  );

  const accountIdsKey = useMemo(
    () => accounts.map((a) => a.accountId).join(','),
    [accounts],
  );
  const [expandedUniqueIds, setExpandedUniqueIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [sharedExpanded, setSharedExpanded] = useState(false);

  useEffect(() => {
    setExpandedUniqueIds(new Set());
    setSharedExpanded(false);
  }, [accountIdsKey]);

  const contentLoading = isComparisonContentLoading(accounts);

  if (contentLoading) {
    return <ComparisonCardSkeleton title="Allocation overlap" lines={6} />;
  }

  if (overlap.totalSymbols === 0) {
    return null;
  }

  const sharedCount = overlap.shared.length;
  const partialCount = overlap.partial.length;
  const uniqueCount = overlap.unique.length;
  const total = overlap.totalSymbols;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

  const uniqueByAccount = accounts.map((acc) => ({
    acc,
    items: overlap.unique
      .filter((u) => u.accountId === acc.accountId)
      .sort((a, b) => b.weight - a.weight),
  }));

  const sharedSorted = [...overlap.shared].sort((a, b) => {
    const aw = Math.max(...Object.values(a.weights));
    const bw = Math.max(...Object.values(b.weights));
    return bw - aw;
  });

  const toggleUniqueExpanded = (accountId: string) => {
    setExpandedUniqueIds((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  return (
    <div className={CARD_CLASS}>
      <div className="mb-3 flex flex-col">
        <h3 className="text-[1.05rem] font-semibold text-foreground">
          Allocation overlap
        </h3>
        <p className="mt-1 text-[0.85rem] text-white/55">
          Across {total} unique symbols held by the selected portfolios.
        </p>
      </div>

      <div
        className="flex h-[18px] w-full overflow-hidden rounded-[9px] border border-white/[0.06] bg-white/[0.04]"
        role="img"
        aria-label="Shared, partial and unique symbols"
      >
        {sharedCount > 0 && (
          <span
            className="inline-flex h-full items-center justify-center bg-positive text-[0.72rem] font-semibold text-black/70"
            style={{ width: `${pct(sharedCount)}%` }}
            title={`${sharedCount} held by all`}
          >
            {sharedCount > 0 && pct(sharedCount) > 8 ? sharedCount : ''}
          </span>
        )}
        {partialCount > 0 && (
          <span
            className="inline-flex h-full items-center justify-center bg-primary text-[0.72rem] font-semibold text-black/70"
            style={{ width: `${pct(partialCount)}%` }}
            title={`${partialCount} held by some`}
          >
            {pct(partialCount) > 8 ? partialCount : ''}
          </span>
        )}
        {uniqueCount > 0 && (
          <span
            className="inline-flex h-full items-center justify-center bg-[color:#a78bfa] text-[0.72rem] font-semibold text-black/70"
            style={{ width: `${pct(uniqueCount)}%` }}
            title={`${uniqueCount} held by exactly one`}
          >
            {pct(uniqueCount) > 8 ? uniqueCount : ''}
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-wrap gap-4 text-[0.85rem] text-white/70">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-[3px] bg-positive"
            aria-hidden
          />
          Shared by all · <strong>{sharedCount}</strong>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-[3px] bg-primary"
            aria-hidden
          />
          Held by some · <strong>{partialCount}</strong>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-[3px] bg-[color:#a78bfa]"
            aria-hidden
          />
          Unique to one · <strong>{uniqueCount}</strong>
        </span>
      </div>

      {uniqueByAccount.some((u) => u.items.length > 0) && (
        <div className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
          {uniqueByAccount.map(({ acc, items }) =>
            items.length === 0 ? null : (
              <div
                key={acc.accountId}
                className="rounded-[8px] border border-white/[0.06] bg-white/[0.02] px-2.5 py-2"
              >
                <div className="mb-1 flex items-center gap-1.5 border-b border-white/[0.06] pb-1">
                  <span
                    className={CHIP_DOT}
                    style={{ backgroundColor: acc.color }}
                    aria-hidden
                  />
                  <span className={UNIQUES_PORTFOLIO}>Only in {acc.label}</span>
                </div>
                <ul className="flex list-none flex-col gap-0.5 text-[0.85rem]">
                  {(expandedUniqueIds.has(acc.accountId)
                    ? items
                    : items.slice(0, UNIQUE_PREVIEW_COUNT)
                  ).map((item) => (
                    <li key={item.symbol} className="flex items-center justify-between">
                      <ComparisonHoverTooltip
                        block
                        content={positionRowTooltipContent(
                          item.symbol,
                          lookupPositionMeta(accounts, acc.accountId, item.symbol),
                          acc.label,
                          undefined,
                          lookupCompanyName(accounts, item.symbol),
                        )}
                      >
                        <span className="flex w-full min-w-0 cursor-help items-center justify-between gap-2">
                          <span className={TICKER}>{item.symbol}</span>
                          <span className={METRIC}>
                            <span className="font-medium">
                              {item.weight.toFixed(2)}%
                            </span>
                            {renderPlPct(item.unrealizedPLPct)}
                          </span>
                        </span>
                      </ComparisonHoverTooltip>
                    </li>
                  ))}
                  {items.length > UNIQUE_PREVIEW_COUNT && (
                    <li className="mt-2 border-t border-white/[0.05] pt-1.5">
                      <button
                        type="button"
                        className={MORE_BTN}
                        onClick={() => toggleUniqueExpanded(acc.accountId)}
                        aria-expanded={expandedUniqueIds.has(acc.accountId)}
                      >
                        {expandedUniqueIds.has(acc.accountId)
                          ? 'Show less'
                          : `+${items.length - UNIQUE_PREVIEW_COUNT} more`}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            ),
          )}
        </div>
      )}

      {sharedSorted.length > 0 && (
        <div className="mt-3 rounded-[8px] border border-white/[0.06] bg-white/[0.02] px-2.5 py-2">
          <div className="mb-1 flex items-center gap-1.5 border-b border-white/[0.06] pb-1">
            <span
              className={CHIP_DOT}
              style={{ backgroundColor: SHARED_HOLDINGS_COLOR }}
              aria-hidden
            />
            <span className={UNIQUES_PORTFOLIO}>Shared holdings</span>
          </div>
          <div className="mb-1 mt-1.5 flex items-center justify-between gap-3 border-b border-white/[0.06] pb-1.5">
            <span className="min-w-0 flex-1" aria-hidden />
            <div className="flex flex-none items-center justify-end gap-4">
              {accounts.map((acc) => (
                <span
                  key={acc.accountId}
                  className="inline-flex min-w-[72px] max-w-[120px] items-center justify-end gap-1.5"
                >
                  <span
                    className={CHIP_DOT}
                    style={{ backgroundColor: acc.color }}
                    aria-hidden
                  />
                  <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[0.85rem] font-semibold">
                    {acc.label}
                  </span>
                </span>
              ))}
            </div>
          </div>
          <ul className="flex list-none flex-col gap-0.5 text-[0.85rem]">
            {(sharedExpanded
              ? sharedSorted
              : sharedSorted.slice(0, SHARED_PREVIEW_COUNT)
            ).map((item) => (
              <li key={item.symbol} className="flex items-center justify-between">
                <ComparisonHoverTooltip
                  block
                  wide
                  placement="bottom"
                  content={sharedHoldingRowTooltipContent(item.symbol, accounts)}
                >
                  <span className="flex w-full min-w-0 cursor-help items-center justify-between gap-3">
                    <span className={TICKER}>{item.symbol}</span>
                    <div className="flex flex-none items-center justify-end gap-4">
                      {accounts.map((acc) => (
                        <span key={acc.accountId} className={METRIC}>
                          {item.weights[acc.accountId] !== undefined ? (
                            <>
                              <span className="font-medium">
                                {item.weights[acc.accountId].toFixed(2)}%
                              </span>
                              {renderPlPct(item.plPcts[acc.accountId])}
                            </>
                          ) : (
                            <span className="text-white/30">—</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </span>
                </ComparisonHoverTooltip>
              </li>
            ))}
            {sharedSorted.length > SHARED_PREVIEW_COUNT && (
              <li className="mt-2 border-t border-white/[0.05] pt-1.5">
                <button
                  type="button"
                  className={MORE_BTN}
                  onClick={() => setSharedExpanded((v) => !v)}
                  aria-expanded={sharedExpanded}
                >
                  {sharedExpanded
                    ? 'Show less'
                    : `+${sharedSorted.length - SHARED_PREVIEW_COUNT} more shared symbols`}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ComparisonAllocationOverlap;
